import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { Transaction } from './useTransactions';

export interface Debt {
  id: string;
  user_id: string;
  name: string;
  creditor: string | null;
  initial_balance: number;
  current_balance: number;
  interest_rate: number;
  minimum_payment: number;
  due_day: number | null;
  status: 'active' | 'paid_off' | 'paused';
  priority_order: number | null;
  paid_off_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface DebtFormData {
  name: string;
  creditor?: string;
  initial_balance: number;
  current_balance: number;
  interest_rate: number;
  minimum_payment: number;
  due_day?: number;
}

export function useDebts() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: debts = [], isLoading, error } = useQuery({
    queryKey: ['debts', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('debts')
        .select('*')
        .eq('user_id', user.id)
        .order('current_balance', { ascending: true });

      if (error) throw error;
      return data as Debt[];
    },
    enabled: !!user?.id,
  });

  const createDebt = useMutation({
    mutationFn: async (debtData: DebtFormData) => {
      if (!user?.id) throw new Error('Usuario no autenticado');

      const { data, error } = await supabase
        .from('debts')
        .insert({
          user_id: user.id,
          ...debtData,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({
        title: 'Deuda agregada',
        description: 'La deuda se ha registrado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudo agregar la deuda.',
        variant: 'destructive',
      });
      console.error('Error creating debt:', error);
    },
  });

  const updateDebt = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Debt> & { id: string }) => {
      const { data, error } = await supabase
        .from('debts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({
        title: 'Deuda actualizada',
        description: 'Los cambios se han guardado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la deuda.',
        variant: 'destructive',
      });
      console.error('Error updating debt:', error);
    },
  });

  const deleteDebt = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('debts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({
        title: 'Deuda eliminada',
        description: 'La deuda se ha eliminado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la deuda.',
        variant: 'destructive',
      });
      console.error('Error deleting debt:', error);
    },
  });

  const markAsPaidOff = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('debts')
        .update({
          status: 'paid_off',
          current_balance: 0,
          paid_off_date: new Date().toISOString().split('T')[0],
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({
        title: '¡Felicidades! 🎉',
        description: '¡Has liquidado una deuda! Sigue así.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudo marcar la deuda como pagada.',
        variant: 'destructive',
      });
      console.error('Error marking debt as paid:', error);
    },
  });

  const bulkCreateDebts = useMutation({
    mutationFn: async (debtsData: DebtFormData[]) => {
      if (!user?.id) throw new Error('Usuario no autenticado');

      const debtsToInsert = debtsData.map(debt => ({
        user_id: user.id,
        name: debt.name,
        creditor: debt.creditor || null,
        initial_balance: debt.initial_balance,
        current_balance: debt.current_balance,
        interest_rate: debt.interest_rate || 0,
        minimum_payment: debt.minimum_payment,
        due_day: debt.due_day || null,
      }));

      const { data, error } = await supabase
        .from('debts')
        .insert(debtsToInsert)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts'] });
    },
    onError: (error) => {
      console.error('Bulk import error:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron importar las deudas.',
        variant: 'destructive',
      });
    },
  });

  // Calculate snowball statistics
  const activeDebts = debts.filter(d => d.status === 'active');
  const paidOffDebts = debts.filter(d => d.status === 'paid_off');
  const totalDebt = activeDebts.reduce((sum, d) => sum + d.current_balance, 0);
  const totalInitialDebt = debts.reduce((sum, d) => sum + d.initial_balance, 0);
  const totalPaid = totalInitialDebt - totalDebt;
  const progressPercentage = totalInitialDebt > 0 ? (totalPaid / totalInitialDebt) * 100 : 0;
  const totalMinimumPayment = activeDebts.reduce((sum, d) => sum + d.minimum_payment, 0);

  // Get payments for a specific debt
  const useDebtPayments = (debtId: string) => {
    return useQuery({
      queryKey: ['debt-payments', debtId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('transactions')
          .select('*')
          .eq('debt_id', debtId)
          .eq('type', 'debt_payment')
          .order('date', { ascending: false })
          .limit(10);

        if (error) throw error;
        return data as Transaction[];
      },
      enabled: !!debtId,
    });
  };

  return {
    debts,
    activeDebts,
    paidOffDebts,
    isLoading,
    error,
    createDebt,
    updateDebt,
    deleteDebt,
    markAsPaidOff,
    bulkCreateDebts,
    totalDebt,
    totalInitialDebt,
    totalPaid,
    progressPercentage,
    totalMinimumPayment,
    useDebtPayments,
  };
}
